import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Formik, FieldArray } from 'formik'
import { useErrorService } from '@abroad/components'
import { ExperienceInputField } from '../form'
import ExperienceDatePicker from '../form/ExperienceDatePicker'
import QuestHOC from './QuestHOC'
import CopyItineraryModal from './CopyItineraryModal'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import ExperienceBadge from './ExperienceBadge'
import { FormikDirtyReporter } from './questAutosave'
import API from '../../utils/API'
import {
  UTCFormatDate,
  UTCformatDateInPlan,
  getCurrentDate,
  getProgramDatesRange,
} from '../../utils/date'
import moment from 'moment-timezone'

const DEFAULT_DESCRIPTION = 'Experience'

const emptyDateEntry = {
  description: '',
  startDate: '',
  endDate: '',
}

const defaultDateEntry = {
  description: DEFAULT_DESCRIPTION,
  startDate: '',
  endDate: '',
}

const defaultValues = {
  programDatesList: [{ ...defaultDateEntry }],
}

const isValidCompleteDate = (dateString) => {
  if (!dateString || dateString === '') {
    return false
  }
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/
  if (!dateRegex.test(dateString)) {
    return false
  }
  return moment(dateString, 'MM/DD/YYYY', true).isValid()
}

const formatDatesForDisplay = (programDatesList) => {
  if (!programDatesList?.length) {
    return defaultValues
  }

  return {
    programDatesList: programDatesList.map((item, idx) => ({
      description: idx === 0 ? DEFAULT_DESCRIPTION : item.description || '',
      startDate: item.startDate ? UTCFormatDate(item.startDate) : '',
      endDate: item.endDate ? UTCFormatDate(item.endDate) : '',
    })),
  }
}

const displayDateToUtcMoment = (dateString) => {
  if (!isValidCompleteDate(dateString)) {
    return null
  }
  return moment.utc(dateString, 'MM/DD/YYYY', true)
}

const displayDateToUtcPlan = (dateString) => {
  const parsed = displayDateToUtcMoment(dateString)
  return parsed ? parsed.format('YYYY-MM-DD') : null
}

const getDefaultDateRange = (programDatesList = []) => {
  const defaultRow = programDatesList?.[0] || {}
  return {
    startDate: defaultRow.startDate || '',
    endDate: defaultRow.endDate || '',
  }
}

const isStartDateAfterEndDate = (startDate, endDate) => {
  if (!isValidCompleteDate(startDate) || !isValidCompleteDate(endDate)) {
    return false
  }

  return moment(startDate, 'MM/DD/YYYY', true).isAfter(
    moment(endDate, 'MM/DD/YYYY', true),
  )
}

const updateDates = (values) => {
  return values.programDatesList.map((item, idx) => {
    const entry = {
      description:
        idx === 0 ? DEFAULT_DESCRIPTION : (item.description || '').trim(),
    }
    const startDate = displayDateToUtcPlan(item.startDate)
    const endDate = displayDateToUtcPlan(item.endDate)
    if (startDate) {
      entry.startDate = startDate
    }
    if (endDate) {
      entry.endDate = endDate
    }
    return entry
  })
}

const isExtraRowPartiallyFilled = (item) => {
  return (
    !!(item.description || '').trim() ||
    !!(item.startDate || '').trim() ||
    !!(item.endDate || '').trim()
  )
}

const getExtraRowErrors = (item) => {
  const itemErrors = {}
  const hasDescription = !!(item.description || '').trim()
  const hasStart = !!(item.startDate || '').trim()
  const hasEnd = !!(item.endDate || '').trim()

  if (!isExtraRowPartiallyFilled(item)) {
    return itemErrors
  }

  if (!hasDescription) {
    itemErrors.description = 'Description is required.'
  }

  if (!hasStart) {
    itemErrors.startDate = 'Start Date is required.'
  } else if (!isValidCompleteDate(item.startDate)) {
    itemErrors.startDate =
      'Start Date must be a complete and valid date (MM/DD/YYYY).'
  }

  if (hasEnd && !isValidCompleteDate(item.endDate)) {
    itemErrors.endDate =
      'End Date must be a complete and valid date (MM/DD/YYYY).'
  }

  if (
    isValidCompleteDate(item.startDate) &&
    isValidCompleteDate(item.endDate)
  ) {
    const start = moment(item.startDate, 'MM/DD/YYYY', true)
    const end = moment(item.endDate, 'MM/DD/YYYY', true)
    if (end.isBefore(start)) {
      itemErrors.endDate = 'End Date cannot be earlier than Start Date.'
    }
  }

  return itemErrors
}

const filterNonEmptyProgramDates = (programDatesList) =>
  programDatesList.filter((item, idx) => {
    if (idx === 0) {
      return true
    }
    return isExtraRowPartiallyFilled(item)
  })

const areDefaultDatesDirty = (values, initial) => {
  const current = getDefaultDateRange(values?.programDatesList)
  const baseline = getDefaultDateRange(initial?.programDatesList)
  return (
    current.startDate !== baseline.startDate ||
    current.endDate !== baseline.endDate
  )
}

const hasQuestItineraryDays = (questDetail) => {
  const days = questDetail?.itinerary || questDetail?.itineraryDays || []
  return Array.isArray(days) && days.length > 0
}

const getDefaultDatesHint = ({ startDate, endDate, isDirty, hasItinerary }) => {
  const hasStart = isValidCompleteDate(startDate)
  const hasEnd = isValidCompleteDate(endDate)

  if (hasStart && hasEnd && isStartDateAfterEndDate(startDate, endDate)) {
    return 'End must be on or after the start date.'
  }
  if (!hasStart || !hasEnd) {
    return 'Set both start and end, then save. Duration and itinerary use this range together.'
  }
  if (isDirty) {
    return hasItinerary
      ? 'Save dates to apply this range. You will then remap the itinerary.'
      : 'Save dates to apply this range and create itinerary days.'
  }
  return 'Duration is calculated from this range. Save again if you change any dates.'
}

const ExperienceDates = ({
  questDetail,
  addUpdateQuest,
  updateQuestDetails,
  isFormSubmitting,
  isSaving,
}) => {
  const Error = useErrorService()
  const [initialValues, setInitialValues] = useState(
    formatDatesForDisplay(questDetail?.programDatesList),
  )
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null)
  const [showItineraryModal, setShowItineraryModal] = useState(false)
  const [isModalSubmitting, setIsModalSubmitting] = useState(false)
  const [removeIndex, setRemoveIndex] = useState(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const formikBagRef = useRef(null)

  useEffect(() => {
    // Only sync from server when programDatesList has real dates. The PUT
    // response may omit this field or return entries without dates; that would
    // clear the pickers until a full page refresh (GET) reloads them.
    const list = questDetail?.programDatesList
    if (!Array.isArray(list) || list.length === 0) {
      return
    }
    const hasAnyDate = list.some((item) => item?.startDate || item?.endDate)
    if (!hasAnyDate) {
      return
    }
    setInitialValues(formatDatesForDisplay(list))
  }, [questDetail])

  const resetPendingChanges = () => {
    setPendingSubmitValues(null)
    setShowItineraryModal(false)
  }

  const handleModalCancel = (resetForm) => {
    if (isModalSubmitting) {
      return
    }
    resetForm({ values: initialValues })
    resetPendingChanges()
  }

  const saveProgramDates = async (programDatesList, nextValues, resetForm) => {
    await updateQuestDetails({ programDatesList })
    setInitialValues(nextValues)
    resetForm({ values: nextValues })
  }

  /**
   * Persist additional dates only. Keeps unsaved default-row edits in the form
   * so Revert / Save dates still apply to the first row alone.
   */
  const autosaveAdditionalDates = async (values, resetForm) => {
    const list = values?.programDatesList
    if (!Array.isArray(list)) {
      return
    }
    const extras = list.slice(1)
    const hasExtraErrors = extras.some(
      (item) => Object.keys(getExtraRowErrors(item)).length > 0,
    )
    if (hasExtraErrors) {
      return
    }

    const defaultBaseline = initialValues.programDatesList[0] || {
      ...defaultDateEntry,
    }
    const nonEmptyExtras = extras.filter(isExtraRowPartiallyFilled)
    const initialExtras = (initialValues.programDatesList || []).slice(1)
    const extrasUnchanged =
      nonEmptyExtras.length === initialExtras.length &&
      nonEmptyExtras.every(
        (item, i) =>
          (item.description || '') === (initialExtras[i].description || '') &&
          (item.startDate || '') === (initialExtras[i].startDate || '') &&
          (item.endDate || '') === (initialExtras[i].endDate || ''),
      )
    if (extrasUnchanged) {
      return
    }

    const nextDisplay = {
      programDatesList: [defaultBaseline, ...nonEmptyExtras],
    }
    const programDatesList = updateDates(nextDisplay)

    await updateQuestDetails({ programDatesList })
    setInitialValues(nextDisplay)
    resetForm({
      values: {
        programDatesList: [list[0] || defaultBaseline, ...nonEmptyExtras],
      },
    })
  }

  const handleItineraryModalConfirm = async (payload, resetForm) => {
    setIsModalSubmitting(true)
    try {
      await addUpdateQuest(
        {
          id: questDetail.id || questDetail._id,
          programDatesList: payload.programDatesList,
        },
        { silent: true },
      )
    } catch (e) {
      // addUpdateQuest already surfaces the error toast.
      setIsModalSubmitting(false)
      return
    }

    try {
      await API.AdminAPI.quests.changeItinerary(
        questDetail.id || questDetail._id,
        {
          days: payload.days,
        },
      )
      setInitialValues({
        programDatesList: pendingSubmitValues.programDatesList,
      })
      resetForm({
        values: { programDatesList: pendingSubmitValues.programDatesList },
      })
      resetPendingChanges()
      // Header already shows save state — skip toast.
      // Notification.showNotification('Experience has been updated!', 'success')
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsModalSubmitting(false)
    }
  }

  const getQuestStatus = () => {
    const { start, end } = getProgramDatesRange(questDetail)

    if (!start || !end) {
      return 'Upcoming'
    }

    const currentDate = UTCformatDateInPlan()
    const startFormatted = UTCformatDateInPlan(start)
    const endFormatted = UTCformatDateInPlan(end)

    if (currentDate < startFormatted) {
      return 'Upcoming'
    } else if (currentDate >= startFormatted && currentDate <= endFormatted) {
      return 'In progress'
    } else {
      return 'Completed'
    }
  }

  const isFormDisabled = () => {
    const status = getQuestStatus()
    return status === 'Completed' || status === 'In progress'
  }

  const saveBusy = !!isFormSubmitting || !!isSaving
  const structuralBusy = isModalSubmitting || isRemoving
  const busy = saveBusy || structuralBusy

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => {
        const errors = {}
        const listErrors = []

        values.programDatesList.forEach((item, idx) => {
          const itemErrors = {}
          const isDefaultRow = idx === 0
          const hasStart = !!(item.startDate || '').trim()
          const hasEnd = !!(item.endDate || '').trim()

          if (isDefaultRow) {
            if (!hasStart) {
              itemErrors.startDate = 'Start Date is required.'
            } else if (!isValidCompleteDate(item.startDate)) {
              itemErrors.startDate =
                'Start Date must be a complete and valid date (MM/DD/YYYY).'
            }

            if (!hasEnd) {
              itemErrors.endDate = 'End Date is required.'
            } else if (!isValidCompleteDate(item.endDate)) {
              itemErrors.endDate =
                'End Date must be a complete and valid date (MM/DD/YYYY).'
            }
          } else {
            Object.assign(itemErrors, getExtraRowErrors(item))
          }

          if (
            isValidCompleteDate(item.startDate) &&
            isValidCompleteDate(item.endDate)
          ) {
            const start = moment(item.startDate, 'MM/DD/YYYY', true)
            const end = moment(item.endDate, 'MM/DD/YYYY', true)
            if (end.isBefore(start)) {
              itemErrors.endDate = 'End Date cannot be earlier than Start Date.'
            }
          }

          if (Object.keys(itemErrors).length > 0) {
            listErrors[idx] = itemErrors
          }
        })

        if (listErrors.length > 0) {
          errors.programDatesList = listErrors
        }

        return errors
      }}
      onSubmit={(values, { resetForm }) => {
        const nonEmptyList = filterNonEmptyProgramDates(values.programDatesList)
        const nextValues = { programDatesList: nonEmptyList }
        // Next flushes this form even when nothing changed — skip the API then.
        if (
          JSON.stringify(nextValues.programDatesList) ===
          JSON.stringify(
            filterNonEmptyProgramDates(initialValues.programDatesList),
          )
        ) {
          return
        }
        const programDatesList = updateDates(nextValues)
        const currentRange = getDefaultDateRange(initialValues.programDatesList)
        const nextRange = getDefaultDateRange(nextValues.programDatesList)
        const hasDefaultDateChanged =
          currentRange.startDate !== nextRange.startDate ||
          currentRange.endDate !== nextRange.endDate
        const hadPreviousDefaultDates =
          isValidCompleteDate(currentRange.startDate) &&
          isValidCompleteDate(currentRange.endDate)

        if (!hasDefaultDateChanged || !hadPreviousDefaultDates) {
          saveProgramDates(programDatesList, nextValues, resetForm)
          return
        }

        setPendingSubmitValues({
          programDatesList: nextValues.programDatesList,
          apiProgramDatesList: programDatesList,
        })
        setShowItineraryModal(true)
      }}>
      {({
        handleSubmit,
        values,
        errors,
        touched,
        resetForm,
        submitCount,
        dirty,
        setFieldValue,
      }) => {
        formikBagRef.current = {
          values,
          resetForm,
          setFieldValue,
        }
        const isDefaultDirty = areDefaultDatesDirty(values, initialValues)
        const defaultRange = getDefaultDateRange(values.programDatesList)
        const hasCompleteDefaultRange =
          isValidCompleteDate(defaultRange.startDate) &&
          isValidCompleteDate(defaultRange.endDate) &&
          !isStartDateAfterEndDate(defaultRange.startDate, defaultRange.endDate)
        const canSaveDefaultDates = isDefaultDirty && hasCompleteDefaultRange
        const defaultDatesHint = getDefaultDatesHint({
          startDate: defaultRange.startDate,
          endDate: defaultRange.endDate,
          isDirty: isDefaultDirty,
          hasItinerary: hasQuestItineraryDays(questDetail),
        })

        const queueAdditionalAutosave = (overrideValues) => {
          window.setTimeout(async () => {
            const bag = formikBagRef.current
            if (!bag || structuralBusy || isFormDisabled()) {
              return
            }
            // Ignore DOM/blur events accidentally passed as the first arg.
            const nextValues =
              overrideValues && Array.isArray(overrideValues.programDatesList)
                ? overrideValues
                : bag.values
            if (!Array.isArray(nextValues?.programDatesList)) {
              return
            }
            const hasExtraErrors = nextValues.programDatesList
              .slice(1)
              .some((item) => Object.keys(getExtraRowErrors(item)).length > 0)
            if (hasExtraErrors) {
              return
            }
            await autosaveAdditionalDates(nextValues, bag.resetForm)
          }, 0)
        }

        const patchExtraDateField = (idx, field, fieldValue) => {
          const nextList = values.programDatesList.map((item, i) =>
            i === idx ? { ...item, [field]: fieldValue } : item,
          )
          return { programDatesList: nextList }
        }

        const confirmRemoveAdditionalDate = async () => {
          if (removeIndex == null) {
            return
          }
          setIsRemoving(true)
          try {
            const nextList = values.programDatesList.filter(
              (_, i) => i !== removeIndex,
            )
            const nextValues = { programDatesList: nextList }
            setFieldValue('programDatesList', nextList, false)
            await autosaveAdditionalDates(nextValues, resetForm)
            setRemoveIndex(null)
          } catch (e) {
            // updateQuestDetails / Error already handled upstream
          } finally {
            setIsRemoving(false)
          }
        }

        return (
          <>
            <form
              onSubmit={handleSubmit}
              className='program-dates-form'
              aria-busy={busy}>
              <FormikDirtyReporter id='setup-dates' dirty={dirty} />
              <FieldArray name='programDatesList'>
                {({ push }) => {
                  const defaultEntry = values.programDatesList[0]
                  const defaultTouched = touched.programDatesList?.[0] || {}
                  const defaultErrors = errors.programDatesList?.[0] || {}
                  const showDefaultError = (field) =>
                    !!(
                      (defaultTouched[field] && defaultErrors[field]) ||
                      (submitCount > 0 && defaultErrors[field])
                    )
                  const defaultStartMoment = displayDateToUtcMoment(
                    defaultEntry?.startDate,
                  )
                  const extraDates = values.programDatesList.slice(1)

                  return (
                    <>
                      <div className='experience-f-row'>
                        <div className='experience-f'>
                          <ExperienceDatePicker
                            name='programDatesList[0].startDate'
                            minimumDate={getCurrentDate()}
                            maximumDate={getCurrentDate()
                              .clone()
                              .add(10, 'years')}
                            label='Experience Starts'
                            placeholder='Add start date'
                            showErrorMessage={showDefaultError('startDate')}
                            errorMessage={
                              showDefaultError('startDate')
                                ? defaultErrors.startDate
                                : undefined
                            }
                            className='experience-f-control text-capitalize'
                            labelProps={{ className: 'experience-f-label' }}
                            displayFlex={false}
                            position='auto'
                            rangeStart={defaultEntry?.startDate}
                            rangeEnd={defaultEntry?.endDate}
                            key={defaultEntry?.endDate || 'start-0-no-end-date'}
                            onValueChange={(nextStartDate) => {
                              if (
                                isStartDateAfterEndDate(
                                  nextStartDate,
                                  defaultEntry?.endDate,
                                )
                              ) {
                                setFieldValue('programDatesList[0].endDate', '')
                              }
                            }}
                            disabled={isFormDisabled() || structuralBusy}
                          />
                        </div>
                        <div className='experience-f'>
                          <ExperienceDatePicker
                            name='programDatesList[0].endDate'
                            label='Experience Ends'
                            placeholder='Add end date'
                            showErrorMessage={showDefaultError('endDate')}
                            errorMessage={
                              showDefaultError('endDate')
                                ? defaultErrors.endDate
                                : undefined
                            }
                            className='experience-f-control text-capitalize'
                            labelProps={{ className: 'experience-f-label' }}
                            displayFlex={false}
                            position='auto'
                            rangeStart={defaultEntry?.startDate}
                            rangeEnd={defaultEntry?.endDate}
                            minimumDate={defaultStartMoment || getCurrentDate()}
                            maximumDate={
                              defaultStartMoment
                                ? defaultStartMoment.clone().add(10, 'years')
                                : getCurrentDate().clone().add(10, 'years')
                            }
                            key={
                              defaultEntry?.startDate || 'end-0-no-start-date'
                            }
                            disabled={isFormDisabled() || structuralBusy}
                          />
                        </div>
                      </div>

                      <div className='experience-date-commit'>
                        <p className='experience-f-hint text-dark'>
                          {defaultDatesHint}
                        </p>
                        {isDefaultDirty ? (
                          <button
                            type='button'
                            className='btn-outline-experience btn-sm-experience'
                            disabled={
                              structuralBusy ||
                              isFormDisabled() ||
                              showItineraryModal
                            }
                            onClick={() => {
                              resetForm({
                                values: {
                                  programDatesList: [
                                    initialValues.programDatesList[0],
                                    ...values.programDatesList.slice(1),
                                  ],
                                },
                              })
                              resetPendingChanges()
                            }}>
                            Revert
                          </button>
                        ) : null}
                        <button
                          type='submit'
                          className='btn-navy btn-sm-experience'
                          disabled={
                            structuralBusy ||
                            isFormDisabled() ||
                            showItineraryModal ||
                            !canSaveDefaultDates
                          }>
                          {isFormSubmitting ? 'Saving…' : 'Save dates'}
                        </button>
                      </div>

                      {extraDates.length > 0 ? (
                        <div className='experience-extra-dates'>
                          {extraDates.map((dateEntry, extraIdx) => {
                            const idx = extraIdx + 1
                            const touchedItem =
                              touched.programDatesList?.[idx] || {}
                            const errorItem =
                              errors.programDatesList?.[idx] || {}
                            const showFieldError = (field) =>
                              !!(
                                (touchedItem[field] && errorItem[field]) ||
                                (submitCount > 0 && errorItem[field])
                              )
                            const showDescriptionError =
                              !!errorItem.description &&
                              (!!touchedItem.description || submitCount > 0)
                            const startMoment = displayDateToUtcMoment(
                              dateEntry.startDate,
                            )

                            return (
                              <div className='experience-extra-date' key={idx}>
                                <div className='experience-extra-date-top'>
                                  <span>Additional date</span>
                                  <ExperienceBadge
                                    title='Participant'
                                    variant='peach'
                                  />
                                  {!isFormDisabled() ? (
                                    <button
                                      type='button'
                                      className='experience-muted-btn'
                                      disabled={structuralBusy}
                                      onClick={() => setRemoveIndex(idx)}>
                                      Remove
                                    </button>
                                  ) : null}
                                </div>
                                <div className='experience-f-row'>
                                  <ExperienceInputField
                                    name={`programDatesList[${idx}].description`}
                                    label='Description'
                                    placeholder='Experience'
                                    showErrorMessage={showDescriptionError}
                                    capitalizeFirstLetter
                                    disabled={
                                      isFormDisabled() || structuralBusy
                                    }
                                    onBlur={() => queueAdditionalAutosave()}
                                  />
                                  <div className='experience-f'>
                                    <ExperienceDatePicker
                                      name={`programDatesList[${idx}].startDate`}
                                      minimumDate={getCurrentDate()}
                                      maximumDate={getCurrentDate()
                                        .clone()
                                        .add(10, 'years')}
                                      label='Starts'
                                      placeholder='Add start date'
                                      showErrorMessage={showFieldError(
                                        'startDate',
                                      )}
                                      errorMessage={
                                        showFieldError('startDate')
                                          ? errorItem.startDate
                                          : undefined
                                      }
                                      className='experience-f-control text-capitalize'
                                      labelProps={{
                                        className: 'experience-f-label',
                                      }}
                                      displayFlex={false}
                                      position='auto'
                                      rangeStart={dateEntry.startDate}
                                      rangeEnd={dateEntry.endDate}
                                      key={
                                        dateEntry.endDate ||
                                        `start-${idx}-no-end-date`
                                      }
                                      onValueChange={(nextStartDate) => {
                                        let nextValues = patchExtraDateField(
                                          idx,
                                          'startDate',
                                          nextStartDate,
                                        )
                                        if (
                                          isStartDateAfterEndDate(
                                            nextStartDate,
                                            dateEntry.endDate,
                                          )
                                        ) {
                                          setFieldValue(
                                            `programDatesList[${idx}].endDate`,
                                            '',
                                          )
                                          nextValues = {
                                            programDatesList:
                                              nextValues.programDatesList.map(
                                                (item, i) =>
                                                  i === idx
                                                    ? { ...item, endDate: '' }
                                                    : item,
                                              ),
                                          }
                                        }
                                        queueAdditionalAutosave(nextValues)
                                      }}
                                      disabled={
                                        isFormDisabled() || structuralBusy
                                      }
                                    />
                                  </div>
                                  <div className='experience-f'>
                                    <ExperienceDatePicker
                                      name={`programDatesList[${idx}].endDate`}
                                      label='Ends'
                                      placeholder='Add end date'
                                      showErrorMessage={showFieldError(
                                        'endDate',
                                      )}
                                      errorMessage={
                                        showFieldError('endDate')
                                          ? errorItem.endDate
                                          : undefined
                                      }
                                      className='experience-f-control text-capitalize'
                                      labelProps={{
                                        className: 'experience-f-label',
                                      }}
                                      displayFlex={false}
                                      position='auto'
                                      rangeStart={dateEntry.startDate}
                                      rangeEnd={dateEntry.endDate}
                                      minimumDate={
                                        startMoment || getCurrentDate()
                                      }
                                      maximumDate={
                                        startMoment
                                          ? startMoment.clone().add(10, 'years')
                                          : getCurrentDate()
                                              .clone()
                                              .add(10, 'years')
                                      }
                                      key={
                                        dateEntry.startDate ||
                                        `end-${idx}-no-start-date`
                                      }
                                      onValueChange={(nextEndDate) => {
                                        queueAdditionalAutosave(
                                          patchExtraDateField(
                                            idx,
                                            'endDate',
                                            nextEndDate,
                                          ),
                                        )
                                      }}
                                      disabled={
                                        isFormDisabled() || structuralBusy
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      ) : null}

                      {!isFormDisabled() ? (
                        <button
                          type='button'
                          className='btn-outline-experience btn-sm-experience'
                          disabled={structuralBusy}
                          onClick={() => {
                            push({ ...emptyDateEntry })
                          }}>
                          Add new date
                        </button>
                      ) : null}
                    </>
                  )
                }}
              </FieldArray>
            </form>
            <CopyItineraryModal
              show={showItineraryModal}
              onHide={() => handleModalCancel(resetForm)}
              selectedQuest={{ value: questDetail?.id }}
              sourceQuestId={questDetail?.id}
              currentQuestStartDate={
                pendingSubmitValues?.apiProgramDatesList?.[0]?.startDate
              }
              currentQuestEndDate={
                pendingSubmitValues?.apiProgramDatesList?.[0]?.endDate
              }
              onConfirmCopy={(payload) =>
                handleItineraryModalConfirm(
                  {
                    ...payload,
                    programDatesList:
                      pendingSubmitValues?.apiProgramDatesList || [],
                  },
                  resetForm,
                )
              }
              isLoading={isModalSubmitting}
              title='Update Itinerary Dates'
              confirmLabel='Save'
              showOriginalLink={false}
            />
            <ExperienceConfirmModal
              show={removeIndex != null}
              onHide={() => {
                if (!isRemoving) setRemoveIndex(null)
              }}
              onConfirm={confirmRemoveAdditionalDate}
              title='Remove this additional date?'
              message='This date range will be removed from the experience.'
              cancelLabel='Cancel'
              confirmLabel='Delete'
              isLoading={isRemoving}
            />
          </>
        )
      }}
    </Formik>
  )
}

ExperienceDates.propTypes = {
  questDetail: PropTypes.shape({
    programDatesList: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
      }),
    ),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  addUpdateQuest: PropTypes.func,
  updateQuestDetails: PropTypes.func,
  isFormSubmitting: PropTypes.bool,
  isSaving: PropTypes.bool,
}

export default QuestHOC(ExperienceDates)
